<template>
  <div>
    <div class="chart" :style="{ height: `${height}px` }" :id="title"></div>
  </div>
</template>

<script>
import { DateTool } from "@js/date-tool.js";
import * as echarts from "echarts/core";
import { LineChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent,
  MarkAreaComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";

// 注册必须的组件
echarts.use([
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent,
  MarkAreaComponent,
  LineChart,
  CanvasRenderer,
]);

export default {
  components: {},

  props: {
    title: String,
    model: Object,
    height: {
      type: Number,
      default: 710, // 增加整体高度
    },
  },

  data() {
    return {
      chartInstance: null,
      colors: {
        breath: "#867cf2", // 更新呼吸波颜色
        bloodOxygen: "#f16162", // 更新血氧颜色
        pulse: "#50ce81", // 更新脉率颜色
        move: "#ff6869", // 更新体动颜色
        breathEvent: "rgba(148, 108, 230, 0.1)", // 呼吸事件颜色，半透明淡紫色
      },
    };
  },

  mounted() {
    this.configureChart();
  },

  methods: {
    configureChart() {
      this.renderChart();
    },

    resizeChart() {
      this.chartInstance.resize();
    },

    renderChart() {
      // 初始化echarts实例
      this.chartInstance = echarts.init(document.getElementById(this.title));

      // 获取数据
      const breathWaveData = this.model.breathWaveChartDataArray;
      const bloodOxygenData = this.model.bloodOxygenChartDataArray;
      const pulseData = this.model.pulseChartDataArray;
      const moveData = this.model.moveRateChartDataArray;
      const timeTickArray = this.model.timeTickArray;
      const breathEventChartDataArray = this.model.breathEventChartDataArray;
      console.log(breathEventChartDataArray.find(item => item.yValue !== 0));

      
      const breathWaveYValue = breathWaveData.map(item => item.yValue);
      const minValue = Math.min(...breathWaveYValue);
      const maxValue = Math.max(...breathWaveYValue);

        // 将最小值向下取整到 500 的倍数
      const yMin = Math.floor(minValue / 100) * 100;
        // 将最大值向上取整到 500 的倍数
      const yMax = Math.ceil(maxValue / 100) * 100;

      const pulseDataYValue = pulseData.map(item => item.yValue);
      const pulseMaxValue = Math.max(...pulseDataYValue);
      //脉率y轴最大值
      const pulseYMax = pulseMaxValue < 140 ? 140 : Math.ceil(pulseMaxValue / 10) * 10;

      let xAxisData = [...timeTickArray];

      // 转换数据格式
      const formatData = (dataArray) => {
        return dataArray.map((item) => {
          return [item.xValue, item.yValue];
        });
      };

      // 处理呼吸事件数据，生成markArea数据
      const processBreathEventData = () => {
        const markAreaData = [];
        
        // 过滤出有效的呼吸事件 (yValue > 0)
        const validEvents = breathEventChartDataArray.filter(item => item.yValue > 0);
        
        validEvents.forEach(event => {
          const startTime = event.xValue;
          const endTime = startTime + event.yValue * 1000; // 结束时间 = 开始时间 + 持续时长
          
          markAreaData.push([
            {
              xAxis: startTime,
            },
            {
              xAxis: endTime,
            }
          ]);
        });
        
        return markAreaData;
      };
      console.log(processBreathEventData());

      // 配置选项
      const option = {
        title: {
          text: this.title,
          left: "center",
          textStyle: {
            fontSize: 26,
            fontWeight: "bold",
          },
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "line",
            snap: true,
            lineStyle: {
              color: "#7E7E7E",
              width: 1,
            },
          },
          formatter: (params) => {
            if (!params || params.length === 0) return "";

            // 找到当前时间点
            const currentTime = params[0].value[0];
            let result =
              DateTool.millisecondTimestampToStr(currentTime, "Y-M-D h:m:s") +
              "<br/>";

            // 收集所有数据系列
            const seriesData = {
              呼吸波: { value: "-", unit: "", color: this.colors.breath },
              血氧: { value: "-", unit: "%", color: this.colors.bloodOxygen },
              脉率: { value: "-", unit: "bpm", color: this.colors.pulse },
              体动: { value: "-", unit: "%", color: this.colors.move },
            };

            // 填充当前参数中有的数据
            params.forEach((param) => {
              if (param.seriesName && param.value[1] !== undefined) {
                seriesData[param.seriesName].value = param.value[1];
              }
            });

            // 按固定顺序构建tooltip内容
            const seriesOrder = ["呼吸波", "血氧", "脉率", "体动"];
            seriesOrder.forEach((name) => {
              const data = seriesData[name];
              const colorStyle = `background-color:${data.color}`;
              result += `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;${colorStyle};"></span> ${name}: ${data.value}${data.unit}<br/>`;
            });

            return result;
          },
        },
        legend: {
          data: ["呼吸波", "血氧", "脉率", "体动"],
          top: 50,
          itemWidth: 20, // 图例图形宽度
          itemHeight: 10, // 图例图形高度
          textStyle: {
            fontSize: 12, // 图例文字大小
          },
          formatter: (name) => {
            return name;
          },
        },
        axisPointer: {
          link: [
            {
              xAxisIndex: "all",
            },
          ],
        },
        grid: [
          // 使用多个grid来分离图表，增加高度和间距
          {
            top: "100",
            height: "110", // 增加高度
            left: "8%",
            right: "8%",
          },
          {
            top: "240", // 增加间距
            height: "110", // 增加高度
            left: "8%",
            right: "8%",
          },
          {
            top: "380", // 增加间距
            height: "110", // 增加高度
            left: "8%",
            right: "8%",
          },
          {
            top: "520", // 增加间距
            height: "110", // 增加高度
            left: "8%",
            right: "8%",
          },
        ],
        xAxis: [
          // 为每个图创建x轴
          {
            gridIndex: 0,
            type: "time",
            boundaryGap: false,
            axisLine: {
              lineStyle: {
                color: "#333",
              },
            },
            axisTick: {
              show: false,
              alignWithLabel: true,
            },
            axisLabel: {
              show: false,
            },
            splitLine: {
              show: false,
            },
            data: xAxisData,
          },
          {
            gridIndex: 1,
            type: "time",
            boundaryGap: false,
            axisLine: {
              lineStyle: {
                color: "#333",
              },
            },
            axisTick: {
              show: false,
              alignWithLabel: true,
            },
            axisLabel: {
              show: false,
            },
            splitLine: {
              show: false,
            },
            data: xAxisData,
          },
          {
            gridIndex: 2,
            type: "time",
            boundaryGap: false,
            axisLine: {
              lineStyle: {
                color: "#333",
              },
            },
            axisTick: {
              show: false,
              alignWithLabel: true,
            },
            axisLabel: {
              show: false,
            },
            splitLine: {
              show: false,
            },
            data: xAxisData,
          },
          {
            gridIndex: 3,
            type: "time",
            boundaryGap: false,
            min: this.xAxisData?.[0],
            max: this.xAxisData?.[this.xAxisData.length - 1],
            axisLine: {
              lineStyle: {
                color: "#333",
              },
            },
            axisTick: {
              show: true,
              alignWithLabel: true,
            },
            axisLabel: {
              formatter: (value) => {
                return DateTool.millisecondTimestampToStr(value, "h:m");
              },
              show: true,
              showMinLabel: true,
              showMaxLabel: true,
            },
            splitLine: {
              show: false,
            },
            data: xAxisData,
          },
        ],
        yAxis: [
          // 呼吸波Y轴 - 修改为黑色
          {
            gridIndex: 0,
            name: "呼吸波",
            nameLocation: "middle",
            nameGap: 45,
            type: "value",
            min: yMin,
            max: yMax,
            position: "left",
            axisLine: {
              lineStyle: {
                color: "#333", // 改为黑色
              },
              show: true,
            },
            axisTick: {
              show: true,
            },
            splitLine: {
              show: true,
            },
          },
          // 血氧Y轴 - 修改为黑色
          {
            gridIndex: 1,
            name: "血氧(%)",
            nameLocation: "middle",
            nameGap: 45,
            type: "value",
            min: 40,
            max: 100,
            position: "left",
            axisLine: {
              lineStyle: {
                color: "#333", // 改为黑色
              },
              show: true,
            },
            axisTick: {
              show: true,
            },
            splitLine: {
              show: true,
            },
          },
          // 脉率Y轴 - 修改为黑色
          {
            gridIndex: 2,
            name: "脉率(bpm)",
            nameLocation: "middle",
            nameGap: 45,
            type: "value",
            min: 20,
            max: pulseYMax,
            position: "left",
            axisLine: {
              lineStyle: {
                color: "#333", // 改为黑色
              },
              show: true,
            },
            axisTick: {
              show: true,
            },
            splitLine: {
              show: true,
            },
          },
          // 体动Y轴 - 修改为黑色
          {
            gridIndex: 3,
            name: "体动(%)",
            nameLocation: "middle",
            nameGap: 45,
            type: "value",
            min: 0,
            max: 60,
            position: "left",
            axisLine: {
              lineStyle: {
                color: "#333", // 改为黑色
              },
              show: true,
            },
            axisTick: {
              show: true,
            },
            splitLine: {
              show: true,
            },
          },
        ],
        dataZoom: [
          {
            type: "inside",
            xAxisIndex: [0, 1, 2, 3], // 所有x轴共用一个数据缩放组件
            start: 0,
            end: 100,
          },
          {
            xAxisIndex: [0, 1, 2, 3], // 所有x轴共用一个数据缩放组件
            start: 0,
            end: 100,
            handleIcon:
              "M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z",
            handleSize: "80%",
            handleStyle: {
              color: "#fff",
              shadowBlur: 3,
              shadowColor: "rgba(0, 0, 0, 0.6)",
              shadowOffsetX: 2,
              shadowOffsetY: 2,
            },
            labelFormatter: (value) => {
              return DateTool.millisecondTimestampToStr(value, "h:m");
            },
            top: "660",
          },
        ],
        series: [
          // 呼吸波
          {
            name: "呼吸波",
            type: "line",
            xAxisIndex: 0,
            yAxisIndex: 0,
            data: formatData(breathWaveData),
            symbol: "none",
            lineStyle: {
              width: 1,
              color: this.colors.breath,
            },
            connectNulls: true,
            itemStyle: {
              color: this.colors.breath,
              borderColor: this.colors.breath,
              borderWidth: 1,
            },
            markArea: {
              itemStyle: {
                color: this.colors.breathEvent,
                borderColor: 'rgba(148, 108, 230, 0.1)',
                borderWidth: 1
              },
              data: processBreathEventData()
            }
          },
          // 血氧
          {
            name: "血氧",
            type: "line",
            xAxisIndex: 1,
            yAxisIndex: 1,
            data: formatData(bloodOxygenData),
            symbol: "none",
            lineStyle: {
              width: 1,
              color: this.colors.bloodOxygen,
            },
            connectNulls: true,
            itemStyle: {
              color: this.colors.bloodOxygen,
              borderColor: this.colors.bloodOxygen,
              borderWidth: 1,
            },
          },
          // 脉率
          {
            name: "脉率",
            type: "line",
            xAxisIndex: 2,
            yAxisIndex: 2,
            data: formatData(pulseData),
            symbol: "none",
            lineStyle: {
              width: 1,
              color: this.colors.pulse,
            },
            connectNulls: true,
            itemStyle: {
              color: this.colors.pulse,
              borderColor: this.colors.pulse,
              borderWidth: 1,
            },
          },
          // 体动
          {
            name: "体动",
            type: "line",
            xAxisIndex: 3,
            yAxisIndex: 3,
            data: formatData(moveData),
            symbol: "none",
            lineStyle: {
              width: 1,
              color: this.colors.move,
            },
            connectNulls: true,
            itemStyle: {
              color: this.colors.move,
              borderColor: this.colors.move,
              borderWidth: 1,
            },
          },
        ],
      };

      // 应用配置
      this.chartInstance.setOption(option);

      // 监听窗口大小变化，自动调整图表大小
      window.addEventListener("resize", this.resizeChart);
    },
  },

  beforeDestroy() {
    // 销毁图表实例
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }

    // 移除事件监听
    window.removeEventListener("resize", this.resizeChart);
  },
};
</script>

<style lang="scss" scoped>
.chart {
  width: 100%;
  box-sizing: border-box;
  padding: 0 rem(25);
  margin-top: rem(10);
  margin-bottom: rem(10);

  @media print {
    display: none;
  }
}
</style>
